import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui';
import { useStableMediaSrc } from '@/hooks/useStableMediaSrc';

// ---------------------------------------------------------------------------
// VideoTrimTool — Performance Window clip-boundary selector
//
// Lets a coach define which portion of the reference video should be analyzed.
// Irrelevant lead-in/cool-down footage outside the IN/OUT handles is excluded
// from phase detection and scoring.
//
// Design notes:
//  - Amber handles (IN / OUT) are visually distinct from PhaseEditor's white
//    phase-boundary handles.
//  - Red playhead (scrub by clicking or dragging on timeline).
//  - Included region is brighter; excluded regions are dimmed.
//  - Minimum clip enforced: at least `fps` frames (≈ 1 second).
//  - Optional suggestion band: if suggestedStart/suggestedEnd are provided, a
//    thin amber band + "Use suggestion" / "Ignore" UI appears.  Suggestions are
//    NEVER silently applied — the coach always confirms.
// ---------------------------------------------------------------------------

export interface VideoTrimToolProps {
  videoUrl: string;
  totalFrames: number;
  fps: number;
  trimStart: number;
  trimEnd: number;
  onTrimChange: (start: number, end: number) => void;
  /**
   * Called when the user clicks the confirm/continue button.
   * When `hideConfirm` is true this button is not rendered and the prop may be
   * omitted — the parent page drives the action (e.g. an "Evaluate" button).
   */
  onConfirm?: () => void;
  /**
   * Hide the in-tool confirm button so the tool is purely controlled via
   * `onTrimChange`.  Used on the evaluation-submission page where the main
   * "Evaluate Performance" button is the commit action.  Default: false.
   */
  hideConfirm?: boolean;
  /**
   * Override the label of the confirm button.  Ignored when `hideConfirm` is
   * true.  Default: "Continue to Phase Detection".
   */
  confirmLabel?: string;
  /** Optional auto-suggested clip range (Phase 3 hook; may be absent) */
  suggestedStart?: number;
  suggestedEnd?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFrameAsTime(frame: number, fps: number): string {
  const totalSeconds = frame / fps;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
}

function formatFrameLabel(frame: number, fps: number): string {
  return `${formatFrameAsTime(frame, fps)} (frame ${frame})`;
}

function formatDuration(startFrame: number, endFrame: number, fps: number): string {
  const durationSeconds = (endFrame - startFrame + 1) / fps;
  if (durationSeconds < 60) return `${durationSeconds.toFixed(1)}s`;
  const minutes = Math.floor(durationSeconds / 60);
  const secs = (durationSeconds % 60).toFixed(1);
  return `${minutes}m ${secs}s`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VideoTrimTool({
  videoUrl,
  totalFrames,
  fps,
  trimStart,
  trimEnd,
  onTrimChange,
  onConfirm,
  hideConfirm = false,
  confirmLabel = 'Continue to Phase Detection',
  suggestedStart,
  suggestedEnd,
}: VideoTrimToolProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const isSettingTimeRef = useRef(false);

  const [currentFrame, setCurrentFrame] = useState(trimStart);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDraggingIn, setIsDraggingIn] = useState(false);
  const [isDraggingOut, setIsDraggingOut] = useState(false);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [suggestionIgnored, setSuggestionIgnored] = useState(false);

  // Minimum clip: at least 1 second worth of frames
  const minClipFrames = Math.max(fps, 1);

  // Keep playback position stable across a signed-URL refresh —
  // see backlog/media-url-refresh-playback-position.md.
  useStableMediaSrc(videoRef, videoUrl);

  // ---------------------------------------------------------------------------
  // Video sync
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (videoRef.current && !isPlaying) {
      isSettingTimeRef.current = true;
      videoRef.current.currentTime = currentFrame / fps;
      const id = setTimeout(() => { isSettingTimeRef.current = false; }, 50);
      return () => clearTimeout(id);
    }
  }, [currentFrame, fps, isPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      if (isSettingTimeRef.current) return;
      const frame = Math.floor(video.currentTime * fps);
      setCurrentFrame(Math.min(frame, totalFrames - 1));

      // Stop preview playback when we reach the OUT point
      if (isPreviewPlaying && frame >= trimEnd) {
        video.pause();
        setIsPreviewPlaying(false);
      }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => { setIsPlaying(false); setIsPreviewPlaying(false); };
    const onEnded = () => { setIsPlaying(false); setIsPreviewPlaying(false); setCurrentFrame(0); };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    };
  }, [fps, totalFrames, trimEnd, isPreviewPlaying]);

  // ---------------------------------------------------------------------------
  // Frame-from-mouse helper (reuses same math as PhaseEditor.getFrameFromMouseEvent)
  // ---------------------------------------------------------------------------

  const getFrameFromMouseEvent = useCallback((e: React.MouseEvent<HTMLDivElement>): number => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    return Math.floor(percent * totalFrames);
  }, [totalFrames]);

  const seekToFrame = useCallback((frame: number) => {
    const clamped = Math.max(0, Math.min(frame, totalFrames - 1));
    setCurrentFrame(clamped);
    if (videoRef.current) videoRef.current.pause();
  }, [totalFrames]);

  // ---------------------------------------------------------------------------
  // Handle drag logic — IN handle
  // ---------------------------------------------------------------------------

  const handleInDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDraggingIn(true);
  };

  const handleOutDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDraggingOut(true);
  };

  const handlePlayheadDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDraggingPlayhead(true);
    if (videoRef.current) videoRef.current.pause();
  };

  const handleTimelineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingIn) {
      const frame = getFrameFromMouseEvent(e);
      const newStart = Math.max(0, Math.min(frame, trimEnd - minClipFrames));
      onTrimChange(newStart, trimEnd);
      setCurrentFrame(newStart);
    } else if (isDraggingOut) {
      const frame = getFrameFromMouseEvent(e);
      const newEnd = Math.max(trimStart + minClipFrames, Math.min(frame, totalFrames - 1));
      onTrimChange(trimStart, newEnd);
      setCurrentFrame(newEnd);
    } else if (isDraggingPlayhead) {
      const frame = getFrameFromMouseEvent(e);
      setCurrentFrame(Math.max(0, Math.min(frame, totalFrames - 1)));
    }
  };

  const handleTimelineMouseUp = () => {
    setIsDraggingIn(false);
    setIsDraggingOut(false);
    setIsDraggingPlayhead(false);
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingIn || isDraggingOut || isDraggingPlayhead) return;
    seekToFrame(getFrameFromMouseEvent(e));
  };

  // ---------------------------------------------------------------------------
  // Preview clip — seeks to IN, plays to OUT, pauses
  // ---------------------------------------------------------------------------

  const handlePreviewClip = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = trimStart / fps;
    setCurrentFrame(trimStart);
    setIsPreviewPlaying(true);
    video.play().catch(() => { setIsPreviewPlaying(false); });
  };

  // ---------------------------------------------------------------------------
  // Suggestion helpers
  // ---------------------------------------------------------------------------

  const hasSuggestion =
    !suggestionIgnored &&
    suggestedStart !== undefined &&
    suggestedEnd !== undefined;

  const handleUseSuggestion = () => {
    if (suggestedStart !== undefined && suggestedEnd !== undefined) {
      onTrimChange(suggestedStart, suggestedEnd);
    }
    setSuggestionIgnored(false);
  };

  const handleIgnoreSuggestion = () => {
    setSuggestionIgnored(true);
  };

  // ---------------------------------------------------------------------------
  // Percentage helpers for timeline layout
  // ---------------------------------------------------------------------------

  const inPercent = (trimStart / totalFrames) * 100;
  const outPercent = (trimEnd / totalFrames) * 100;
  const playheadPercent = (currentFrame / totalFrames) * 100;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-dark-elevated-1 overflow-hidden"
      data-testid="video-trim-tool"
    >
      {/* Panel header */}
      <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-sm text-amber-400">Set your clip</h3>
          <p className="text-xs text-text-muted mt-0.5">
            Trim off the lead-in or cool-down that isn't part of the movement — phase detection only sees the clip you set.
          </p>
        </div>
        <div
          className="flex-shrink-0 w-4 h-4 rounded-full border-2 border-amber-400"
          aria-hidden="true"
          style={{ background: 'transparent' }}
        />
      </div>

      <div className="p-4 space-y-4">
        {/* Suggestion banner — only when suggestions are present and not ignored */}
        {hasSuggestion && (
          <div
            className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-center justify-between gap-3"
            data-testid="suggestion-banner"
          >
            <div className="text-xs text-amber-300">
              <span className="font-medium">Suggested clip: </span>
              {formatFrameLabel(suggestedStart!, fps)} – {formatFrameLabel(suggestedEnd!, fps)}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleUseSuggestion}
                data-testid="use-suggestion-button"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 text-xs py-1 px-2"
              >
                Use suggestion
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleIgnoreSuggestion}
                data-testid="ignore-suggestion-button"
                className="text-text-muted text-xs py-1 px-2"
              >
                Ignore
              </Button>
            </div>
          </div>
        )}

        {/* Video player */}
        <div className="bg-black rounded-lg overflow-hidden aspect-video">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full"
            playsInline
          />
        </div>

        {/* Play / pause controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (videoRef.current) {
                if (isPlaying) videoRef.current.pause();
                else videoRef.current.play().catch(() => {});
              }
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-dark-elevated-2 hover:bg-dark-elevated-1 text-text-secondary hover:text-text-primary transition-colors"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <span className="text-xs text-text-muted flex-1">
            {formatFrameLabel(currentFrame, fps)}
          </span>
        </div>

        {/* Timeline */}
        <div className="space-y-1">
          <div
            ref={timelineRef}
            className="relative h-10 rounded cursor-pointer select-none overflow-visible"
            style={{ background: 'var(--color-surface-raised-2, #1e2128)' }}
            onClick={handleTimelineClick}
            onMouseMove={handleTimelineMouseMove}
            onMouseUp={handleTimelineMouseUp}
            onMouseLeave={handleTimelineMouseUp}
            data-testid="trim-timeline"
          >
            {/* Excluded region — left of IN handle */}
            <div
              className="absolute top-0 left-0 h-full rounded-l"
              style={{
                width: `${inPercent}%`,
                background: 'rgba(0,0,0,0.5)',
              }}
              data-testid="excluded-left"
            />

            {/* Excluded region — right of OUT handle */}
            <div
              className="absolute top-0 h-full rounded-r"
              style={{
                left: `${outPercent}%`,
                right: 0,
                background: 'rgba(0,0,0,0.5)',
              }}
              data-testid="excluded-right"
            />

            {/* Included region — between IN and OUT */}
            <div
              className="absolute top-0 h-full"
              style={{
                left: `${inPercent}%`,
                width: `${outPercent - inPercent}%`,
                background: 'rgba(245, 158, 11, 0.18)',
                borderTop: '1px solid rgba(245,158,11,0.4)',
                borderBottom: '1px solid rgba(245,158,11,0.4)',
              }}
              data-testid="included-region"
            />

            {/* Suggestion band — thin amber highlight */}
            {hasSuggestion && suggestedStart !== undefined && suggestedEnd !== undefined && (
              <div
                className="absolute top-0 h-full pointer-events-none"
                style={{
                  left: `${(suggestedStart / totalFrames) * 100}%`,
                  width: `${((suggestedEnd - suggestedStart) / totalFrames) * 100}%`,
                  borderTop: '2px solid rgba(245,158,11,0.8)',
                  borderBottom: '2px solid rgba(245,158,11,0.8)',
                  background: 'rgba(245,158,11,0.08)',
                }}
                data-testid="suggestion-band"
              />
            )}

            {/* IN handle — amber, left edge of clip */}
            <div
              className="absolute top-0 h-full flex items-center justify-center cursor-ew-resize z-10"
              style={{
                left: `${inPercent}%`,
                transform: 'translateX(-50%)',
                width: '14px',
              }}
              onMouseDown={handleInDragStart}
              data-testid="in-handle"
            >
              <div
                className="w-3 h-full rounded-sm flex items-center justify-center"
                style={{ background: 'rgb(245,158,11)', opacity: 0.95 }}
              >
                <svg className="w-2 h-3 text-black" fill="currentColor" viewBox="0 0 8 12">
                  <path d="M3 0v12M0 4l3-4 3 4" />
                </svg>
              </div>
            </div>

            {/* OUT handle — amber, right edge of clip */}
            <div
              className="absolute top-0 h-full flex items-center justify-center cursor-ew-resize z-10"
              style={{
                left: `${outPercent}%`,
                transform: 'translateX(-50%)',
                width: '14px',
              }}
              onMouseDown={handleOutDragStart}
              data-testid="out-handle"
            >
              <div
                className="w-3 h-full rounded-sm flex items-center justify-center"
                style={{ background: 'rgb(245,158,11)', opacity: 0.95 }}
              >
                <svg className="w-2 h-3 text-black" fill="currentColor" viewBox="0 0 8 12">
                  <path d="M5 0v12M8 8l-3 4-3-4" />
                </svg>
              </div>
            </div>

            {/* Red playhead (matches PhaseEditor style) */}
            <div
              className="absolute top-0 w-0.5 h-full bg-red-500 z-20"
              style={{ left: `${playheadPercent}%` }}
            >
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-red-500 rounded-full cursor-grab active:cursor-grabbing hover:scale-110 transition-transform shadow-md"
                onMouseDown={handlePlayheadDragStart}
                data-testid="playhead"
              />
            </div>
          </div>

          <p className="text-xs text-text-muted">
            Drag red playhead to scrub. Drag amber handles to set IN/OUT points.
          </p>
        </div>

        {/* In/out readouts + clip duration chip */}
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ background: 'rgb(245,158,11)' }}
              aria-hidden="true"
            />
            <span className="text-text-muted">IN:</span>
            <span className="font-mono text-text-primary" data-testid="in-readout">
              {formatFrameLabel(trimStart, fps)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ background: 'rgb(245,158,11)' }}
              aria-hidden="true"
            />
            <span className="text-text-muted">OUT:</span>
            <span className="font-mono text-text-primary" data-testid="out-readout">
              {formatFrameLabel(trimEnd, fps)}
            </span>
          </div>
          <span
            className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30"
            data-testid="clip-duration"
          >
            {formatDuration(trimStart, trimEnd, fps)} clip
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handlePreviewClip}
            disabled={isPreviewPlaying}
            className="text-xs"
          >
            {isPreviewPlaying ? 'Previewing...' : 'Preview clip'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onTrimChange(0, totalFrames - 1)}
            className="text-xs text-text-muted"
            data-testid="reset-button"
          >
            Reset to full video
          </Button>
          {!hideConfirm && (
            <Button
              size="sm"
              variant="primary"
              onClick={onConfirm}
              className="ml-auto bg-accent hover:bg-accent-hover text-text-inverse text-xs font-semibold px-4"
              data-testid="confirm-button"
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
